import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { EXPENSE_CATEGORIES, expenseCategoryMeta } from '@/lib/finance';

/**
 * useExpenseCategories
 * --------------------
 * Les types de dépense viennent de la base (finance_expense_categories).
 * Chaque commerçant voit :
 *   - les catégories globales (merchant_id NULL, définies par la plateforme) ;
 *   - ses propres catégories (merchant_id = son id).
 * Il peut ajouter un nouveau type, qui n'apparaîtra que dans SON compte.
 */

export interface ExpenseCategory {
  id: number;
  merchant_id: number | null;
  key: string;
  label: string;
  icon: string;
  sort_order: number;
  is_default: boolean;
}

export interface ExpenseCategoryMeta {
  key: string;
  label: string;
  icon: string;
}

const DEFAULT_ICON = 'ri-price-tag-3-line';

/** Construit une clé technique stable à partir d'un libellé. */
function slugify(label: string): string {
  return (
    (label || 'categorie')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'categorie'
  );
}

export function useExpenseCategories() {
  const { user } = useAuth();
  const merchantId = user?.id ?? null;
  const [rows, setRows] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('finance_expense_categories').select('*');
      query = merchantId
        ? query.or(`merchant_id.is.null,merchant_id.eq.${merchantId}`)
        : query.is('merchant_id', null);
      const { data, error: err } = await query.order('sort_order', { ascending: true });
      if (err) throw err;
      setRows((data as ExpenseCategory[]) || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement des types de dépense');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Liste fusionnée (globale + perso, la perso prime sur la globale). */
  const categories = useMemo<ExpenseCategoryMeta[]>(() => {
    if (rows.length === 0) return EXPENSE_CATEGORIES.map((c) => ({ key: c.key, label: c.label, icon: c.icon }));
    const map = new Map<string, ExpenseCategoryMeta>();
    rows.forEach((r) => map.set(r.key, { key: r.key, label: r.label, icon: r.icon }));
    return Array.from(map.values());
  }, [rows]);

  /** Résout le libellé + l'icône d'une catégorie (repli sur les défauts). */
  const metaFor = useCallback(
    (key: string | null | undefined): ExpenseCategoryMeta => {
      const found = categories.find((c) => c.key === key);
      return found || expenseCategoryMeta(key);
    },
    [categories],
  );

  const addCategory = useCallback(
    async (label: string, icon: string = DEFAULT_ICON): Promise<ExpenseCategory> => {
      const trimmed = label.trim();
      if (!trimmed) throw new Error('Veuillez saisir un nom de type de dépense.');
      if (!merchantId) throw new Error('Non connecté');
      if (trimmed.length > 40) throw new Error('Le nom est trop long (40 caractères max).');

      const existing = new Set(categories.map((c) => c.key));
      const base = slugify(trimmed);
      let key = base;
      let i = 2;
      while (existing.has(key)) {
        key = `${base}_${i}`;
        i += 1;
      }
      const maxOrder = rows.reduce((m, r) => Math.max(m, r.sort_order || 0), 0);

      const { data, error: err } = await supabase
        .from('finance_expense_categories')
        .insert({
          merchant_id: merchantId,
          key,
          label: trimmed,
          icon: icon || DEFAULT_ICON,
          sort_order: maxOrder + 1,
          is_default: false,
        })
        .select('*')
        .single();
      if (err) throw err;
      const created = data as ExpenseCategory;
      setRows((prev) => [...prev, created]);
      return created;
    },
    [merchantId, categories, rows],
  );

  return { categories, metaFor, loading, error, reload: load, addCategory };
}